import { isRewardAppOnlyBlocked } from '@/utils/rewards';
import {
  isRunningInNativeApp,
  isRunningInIosNativeApp,
} from '@/utils/browserInfo';

// A API só libera o resgate de bônus `is_app_only` quando reconhece que a
// chamada veio do app, e o sinal disso é o header `X-App-Source`. Valores
// aceitos: app, mobile_app, mobileapp, mobile-app, app_android, app_ios e
// native_app — mandamos o da plataforma pro backend distinguir iOS de Android.
//
// O header entra aqui, envolvendo APENAS esta chamada, e sai logo depois. O
// caminho natural seria o `ApiService`, que monta os headers comuns, mas ele
// fica no `_cactus_core`: caminho restrito pelo ruleset e sincronizado do
// `front-web-base`, então uma alteração lá não subiria por aqui. E o
// `ApiService.post()` não aceita headers por chamada — daí envolver o `$fetch`
// no momento do envio e restaurar no `finally`, em vez de deixar um wrapper
// permanente no caminho de toda requisição do app.
async function comHeaderDoApp<T>(enviar: () => Promise<T>): Promise<T> {
  const original = globalThis.$fetch;

  if (!isRunningInNativeApp() || !original) {
    return enviar();
  }

  const valor = isRunningInIosNativeApp() ? 'app_ios' : 'app_android';

  const marcado = ((request: unknown, options: any = {}) => {
    try {
      const anterior = options.onRequest;

      options.onRequest = async (ctx: any) => {
        if (Array.isArray(anterior)) {
          for (const fn of anterior) {
            await fn?.(ctx);
          }
        } else {
          await anterior?.(ctx);
        }

        // depois do `onRequest` do ApiService de propósito: ele SUBSTITUI
        // `options.headers`, então marcar antes seria descartado.
        const headers = ctx?.options?.headers;

        if (headers instanceof Headers) {
          headers.set('X-App-Source', valor);
        } else if (headers && typeof headers === 'object') {
          headers['X-App-Source'] = valor;
        }
      };
    } catch {
      // o header é acessório: nunca pode impedir o envio
    }

    return (original as (r: unknown, o: unknown) => unknown)(request, options);
  }) as typeof original;

  Object.assign(marcado, original);
  (globalThis as any).$fetch = marcado;

  try {
    return await enviar();
  } finally {
    // restaura sempre, inclusive se a chamada falhar
    (globalThis as any).$fetch = original;
  }
}

export const useRewards = () => {
  const {
    isLoading,
    rewards: rewardsFromApi,
    rewrdsPagination,
  } = storeToRefs(useRewardsStore());

  async function fetchRewardsFromApi(
    status: string = 'available',
    type: string = '',
    page: number = 1,
  ) {
    if (isLoading.value) {
      return;
    }

    isLoading.value = true;

    try {
      const { ApiService } = useApiService();

      if (type === 'all') {
        type = '';
      }

      await ApiService.get(
        `/bff/gamification/rewards?status=${status}&type=${type}&page=${page}&per_page=8`,
      )
        .then(({ data }) => {
          if (status === 'available') {
            rewardsFromApi.value.available = data.data;
            rewrdsPagination.value.available = {
              current_page: data.current_page,
              from: data.from,
              last_page: data.last_page,
              per_page: data.per_page,
              to: data.to,
              total: data.total,
              filter: type || 'all',
            };
          } else {
            rewardsFromApi.value.finisheds = data.data;
            rewrdsPagination.value.finisheds = {
              current_page: data.current_page,
              from: data.from,
              last_page: data.last_page,
              per_page: data.per_page,
              to: data.to,
              total: data.total,
              filter: type || 'all',
            };
          }
        })
        .catch((error) => {
          console.error('fetchRewardsFromApi error: ', error);
        })
        .finally(() => {
          isLoading.value = false;
        });
    } catch (err) {
      console.error('fetchRewardsFromApi err: ', err);
      isLoading.value = false;
    }
  }

  async function redeemReward(rewardId: number) {
    // ENG-799: defesa em profundidade do bônus app-only. A UI já trava o
    // botão, mas a lista aparece em TRÊS lugares (Minha Conta V2, a tela
    // legada e o Clube Fidelidade, que monta a lista legada dentro de um
    // modal) — este é o único ponto por onde as três passam.
    //
    // Silencioso de propósito: quem chega aqui com item bloqueado veio por
    // caminho programático, não por clique. Toaster seria ruído.
    //
    // Item que não está na lista NÃO é bloqueado: o guard é rede de
    // segurança, não autoridade sobre o que existe.
    const reward = rewardsFromApi.value.available.find(
      (item) => item.id === rewardId,
    );

    if (reward && isRewardAppOnlyBlocked(reward)) {
      return;
    }

    const { ApiService } = useApiService();

    const response = await comHeaderDoApp(() =>
      ApiService.post(`/bff/gamification/redeem`, { reward_id: rewardId }),
    );

    if (response?.data?.success) {
      rewardsFromApi.value.available = rewardsFromApi.value.available.map(
        (reward) => {
          if (reward.id === rewardId) {
            return {
              ...reward,
              status: 'redeemed',
              updated_at: new Date().toISOString(),
            };
          }

          return reward;
        },
      );

      const { getWallet } = useWalletStore();

      await getWallet();
    }

    return response;
  }

  return {
    fetchRewardsFromApi,
    rewardsFromApi,
    redeemReward,
    isLoadingRewards: isLoading,
    rewrdsPagination,
  };
};
